import { Tooltip } from "@svg/antd";
import { useIntl } from "@umijs/max";
import { useEffect, useRef, useState } from "react";
import "./style.less";

export interface SearchBadcaseFloatButtonProps {
  enabled: boolean;
  visible: boolean;
  expanded: boolean;
  reasons: readonly string[];
  onToggle: () => void;
  onReport: (reason: string) => void;
}

const searchBadcaseIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M8.00067 1.59998C8.33204 1.59998 8.60067 1.8686 8.60067 2.19998C8.60067 2.50373 8.37495 2.75477 8.08209 2.7945L8.00067 2.79998H3.5991V13.2H12.3991L12.3999 7.99998C12.3999 7.69622 12.6256 7.44518 12.9185 7.40545L12.9999 7.39998C13.3037 7.39998 13.5547 7.6257 13.5944 7.91856L13.5999 7.99998V13.6C13.5999 14.0418 13.2417 14.4 12.7999 14.4H3.1999C2.75807 14.4 2.3999 14.0418 2.3999 13.6V2.39998C2.3999 1.95815 2.75807 1.59998 3.1999 1.59998H8.00067Z"
      fill="currentColor"
    />
    <path
      d="M11.4969 2.57462C11.3401 2.73062 11.3401 2.98422 11.4969 3.14022L12.6281 4.27142C12.7841 4.42742 13.0377 4.42742 13.1937 4.27142L13.7593 3.70582C13.9153 3.54982 13.9153 3.29622 13.7593 3.14022L12.6281 2.00902C12.4721 1.85302 12.2185 1.85302 12.0625 2.00902L11.4969 2.57462Z"
      fill="currentColor"
    />
    <path
      d="M8.38134 5.68982L10.3653 3.70582C10.5213 3.54982 10.7749 3.54982 10.9309 3.70582L12.0621 4.83702C12.2181 4.99302 12.2181 5.24742 12.0621 5.40342L10.0781 7.38742L8.39814 7.86422C8.09734 7.94982 7.81894 7.67142 7.90454 7.36982L8.38134 5.68982Z"
      fill="currentColor"
    />
    <path
      d="M6.1999 9.59998C5.86853 9.59998 5.5999 9.86861 5.5999 10.2C5.5999 10.5313 5.86853 10.8 6.1999 10.8H9.7999C10.1313 10.8 10.3999 10.5313 10.3999 10.2C10.3999 9.86861 10.1313 9.59998 9.7999 9.59998H6.1999Z"
      fill="currentColor"
    />
  </svg>
);

export default function SearchBadcaseFloatButton({
  enabled,
  visible,
  expanded,
  reasons,
  onToggle,
  onReport,
}: SearchBadcaseFloatButtonProps) {
  const [showHint, setShowHint] = useState(false);
  const closeMenuTimerRef = useRef<number | undefined>(undefined);
  const { formatMessage } = useIntl();

  const clearCloseMenuTimer = () => {
    if (closeMenuTimerRef.current !== undefined) {
      window.clearTimeout(closeMenuTimerRef.current);
      closeMenuTimerRef.current = undefined;
    }
  };

  const scheduleCloseMenu = () => {
    clearCloseMenuTimer();
    closeMenuTimerRef.current = window.setTimeout(() => {
      closeMenuTimerRef.current = undefined;
      if (expanded) onToggle();
    }, 250);
  };

  useEffect(() => () => clearCloseMenuTimer(), []);

  useEffect(() => {
    if (!visible) {
      setShowHint(false);
      return;
    }
    setShowHint(true);
    const timer = window.setTimeout(() => setShowHint(false), 5000);
    return () => window.clearTimeout(timer);
  }, [visible]);

  if (!enabled || !visible) return null;

  return (
    <div
      className="search-badcase-float-button"
      onMouseEnter={() => {
        clearCloseMenuTimer();
        setShowHint(false);
        if (!expanded) onToggle();
      }}
      onMouseLeave={scheduleCloseMenu}
    >
      {expanded && (
        <div
          className="search-badcase-float-button__menu"
          onMouseEnter={clearCloseMenuTimer}
          onMouseLeave={scheduleCloseMenu}
        >
          {reasons.map((reason) => (
            <button type="button" key={reason} onClick={() => onReport(reason)}>
              {formatMessage({ id: reason })}
            </button>
          ))}
        </div>
      )}
      <div className="search-badcase-float-button__trigger-row">
        <Tooltip
          title={formatMessage({ id: "找不到素材？点击这里帮你找素材" })}
          placement="left"
          trigger={[]}
          open={showHint && !expanded}
        >
          <button
            type="button"
            className="search-badcase-float-button__trigger"
            aria-label={formatMessage({ id: "帮我找素材" })}
          >
            {searchBadcaseIcon}
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
